import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { SubTaskItem, SubTasksEvents } from '../../networkSubTasks/types';
import {
  PanelConfigProps,
  PanelDetailsProps,
  RiskManagementExtraDetails,
  RunLiveSearchQueryExtraDetails,
} from '../types';
import { WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { TaskType } from '../../networkTasks/types';
import { translateFn } from '../../../utils/translator.helper';

export class SubTaskDetailsPanelWebComponent extends LitElement {
  @property({ type: Object, attribute: true, reflect: true })
  panelConfigProps: PanelConfigProps = {
    closeButtonLabel: '',
  };

  @property({ type: Object, attribute: true, reflect: true })
  subTask?: SubTaskItem | null = null;

  @property({ type: Object, attribute: true, reflect: true })
  panelDetailsProps: PanelDetailsProps = {
    company: '',
    createdOn: '',
    endedOn: '',
    endpointName: '',
    awsSubscriptionName: '',
    linkToErrorCodesLabel: '',
    linkToErrorCodesText: '',
    linkToErrorCodesUrl: '',
    status: '',
    containerNames: '',
    statusMessage: '',
    ransomwareRestoreFilesMessage: null,
    scheduledLabel: '',
    scheduled: '',
    paths: [],
    commandLines: [],
    submitToSandboxInvalidFiles: null,
    suspendDuration: null,
    riskManagementExtraDetails: null,
    runLiveSearchQueryExtraDetails: null,
    updateBehavioralProfilesDetails: null,
  };

  static get styles() {
    return styles;
  }

  protected render() {
    return html`
      <div class="details-panel-wrapper">
        <div class="details-panel">${this.renderHeader()} ${this.renderContent()}</div>
      </div>
    `;
  }

  private renderHeader(): TemplateResult {
    return html`
      <div class="details-panel-header">
        <gz-title class="title" fontweight="medium" fontsize="16">${translateFn('Details')}</gz-title>
      </div>
    `;
  }

  private renderContent(): TemplateResult {
    return html` ${this.renderGeneralSection()} ${this.renderButtonsSection()} `;
  }

  private renderGeneralSection(): TemplateResult {
    return html`
        <gz-labeled-content label="${translateFn('Computer name')}">
          <gz-tooltip tooltipText="${this.panelDetailsProps.endpointName}" isEllipsis>
            <gz-text class="labeled-value" fontSize="12">${this.panelDetailsProps.endpointName}</gz-text>
          </gz-tooltip>
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Status')}">
          <gz-text class="labeled-value" fontSize="12">${this.panelDetailsProps.status}</gz-text>
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Company')}">
          <gz-tooltip tooltipText="${this.panelDetailsProps.company}" isEllipsis>
            <gz-text class="labeled-value" fontSize="12">${this.panelDetailsProps.company}</gz-text>
          </gz-tooltip>
        </gz-labeled-content>
        ${this.renderAwsSubscription()}
        <gz-labeled-content label="${translateFn('Created on')}">
          <gz-text class="labeled-value" fontSize="12">${this.panelDetailsProps.createdOn}</gz-text>
        </gz-labeled-content>
        ${this.renderScheduled()}
        ${this.renderEndedOn()}
        ${this.renderContainerNames()}
        ${this.renderDetailsForMoveEndpointChildTaskType()}
        ${this.renderFileName()}
        ${this.renderPaths()}
        ${this.renderCommandLines()}
        ${this.renderSuspendDuration()}
      <br />
      <gz-text id="statusMessage" fontSize="13">${this.panelDetailsProps.statusMessage}</gz-text>
      <gz-text id="customMessage" fontSize="13">${this.panelDetailsProps.linkToErrorCodesLabel}</gz-text>
      <gz-link id="linkToArticle" href="${this.panelDetailsProps.linkToErrorCodesUrl}">${this.panelDetailsProps.linkToErrorCodesText}</gz-link>
      ${this.renderRansomwareRestoreFiles()} ${this.renderSubmitToSandboxInvalidFiles()}
      ${this.renderRiskManagementExtraDetails()} ${this.renderUpdateBehavioralProfilesDetailsItems()}
      ${this.renderRunLiveSearchQueryExtraDetails()}
    `;
  }

  private renderRansomwareRestoreFiles(): TemplateResult | string {
    if (!this.panelDetailsProps.ransomwareRestoreFilesMessage) {
      return html``;
    }

    return html`
      <gz-text id="ransomwareText" fontSize="13"
        >${this.panelDetailsProps.ransomwareRestoreFilesMessage.message}</gz-text
      >
      <ul>
        <li>
          <gz-text id="ransomwareMessageReason1" fontSize="13"
            >${this.panelDetailsProps.ransomwareRestoreFilesMessage.reasons[0]}</gz-text
          >
        </li>
        <li>
          <gz-text id="ransomwareMessageReason2" fontSize="13"
            >${this.panelDetailsProps.ransomwareRestoreFilesMessage.reasons[1]}</gz-text
          >
        </li>
      </ul>
      <gz-link id="ransomwareMoreDetailsLink" @click="${e => this.onRansomwareRestoreFilesMoreDetailsClick(e)}">
        ${translateFn('More details')}
      </gz-link>
    `;
  }

  private onRansomwareRestoreFilesMoreDetailsClick(e: WebComponentEvent) {
    this.dispatchEvent(
      new CustomEvent(SubTasksEvents.OPEN_RANSOMWARE_GRID, {
        bubbles: true,
        composed: true,
        detail: {
          rowDetails: this.subTask,
        },
      })
    );
    this.closePanel();
  }

  private renderSubmitToSandboxInvalidFiles() {
    if (!this.panelDetailsProps.submitToSandboxInvalidFiles) {
      return html``;
    }

    return html`
      ${this.panelDetailsProps.submitToSandboxInvalidFiles.map(
        (invalidFile: { path: string; error: string }) =>
          html`<br />
            <gz-text class="d-inline" fontSize="13" fontWeight="600">${invalidFile.path}:</gz-text>
            <gz-text class="d-inline" fontSize="13">${invalidFile.error}</gz-text>`
      )}
    `;
  }

  private renderRiskManagementExtraDetails() {
    if (isEmpty(this.panelDetailsProps.riskManagementExtraDetails)) {
      return nothing;
    }

    const type = this.subTask?.type;
    const { title, routines } = this.panelDetailsProps.riskManagementExtraDetails as RiskManagementExtraDetails;

    const items = routines.map(routine => {
      const value =
        type === TaskType.TASK_TYPE_IOR_FIX_CHILD
          ? BaseWebComponentTranslator.getInstance().translateRiskManagementFixRoutineResult(routine.result)
          : BaseWebComponentTranslator.getInstance().translateRiskManagementRollbackRoutineResult(routine.result);

      return {
        label: Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineName(
          routine.id
        ),
        value,
      };
    });

    return html`
      <gz-title
        fontSize="${FontSizes.PARAGRAPH_11}"
        fontWeight="${FontWeights.SEMIBOLD}"
        class="section-title text-uppercase mb-2 border-top pt-1"
        >${title}</gz-title
      >
      ${this.renderRiskManagementExtraDetailsItems(items)}
    `;
  }

  private renderRiskManagementExtraDetailsItems(items: { label: string; value: string }[]): TemplateResult<1>[] {
    return items.map(item => {
      return html`
        <gz-labeled-content label="${item.label}">
          <gz-tooltip class="text-end" tooltipText="${item.value}" isEllipsis>
            <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.BOLD}">${item.value}<gz-text>
          </gz-tooltip>
        </gz-labeled-content>
        `;
    });
  }

  private renderRunLiveSearchQueryExtraDetails() {
    if (isEmpty(this.panelDetailsProps.runLiveSearchQueryExtraDetails)) {
      return nothing;
    }

    const { query, s3Bucket, s3Region, s3ObjectUrl } = this.panelDetailsProps.runLiveSearchQueryExtraDetails as RunLiveSearchQueryExtraDetails;

    return html`
      <gz-labeled-content label="${translateFn('Query')}">
        <gz-text class="labeled-value" fontSize="12">${query}</gz-text>
      </gz-labeled-content>
      <gz-labeled-content label="${translateFn('S3 bucket')}">
        <gz-text class="labeled-value" fontSize="12">${s3Bucket}</gz-text>
      </gz-labeled-content>
      <gz-labeled-content label="${translateFn('S3 region')}">
        <gz-text class="labeled-value" fontSize="12">${s3Region}</gz-text>
      </gz-labeled-content>
      <gz-labeled-content label="${translateFn('S3 object URL')}">
        <gz-copy-to-clipboard
          .confirmationMessage="${translateFn('Copied')}"
          .clipboardMessage="${s3ObjectUrl}"
        >
          <gz-tooltip tooltipText="${s3ObjectUrl}" isEllipsis>
            <gz-text class="labeled-value" fontSize="12">${s3ObjectUrl}</gz-text>
          </gz-tooltip>
        </gz-copy-to-clipboard>

      </gz-labeled-content>
    `;
  }

  private renderUpdateBehavioralProfilesDetailsItems(): TemplateResult | string{
    return this.panelDetailsProps.updateBehavioralProfilesDetails && !isEmpty(this.panelDetailsProps.updateBehavioralProfilesDetails.users)
      ? html` <gz-labeled-content label="${translateFn('User profiles')}">
          <div class="d-flex flex-column labeled-value">
            ${this.panelDetailsProps.updateBehavioralProfilesDetails.users.map(userSid => this.renderTextWithTooltip(userSid))}
          </div>
        </gz-labeled-content>`
      : '';
  }

  private renderDetailsForMoveEndpointChildTaskType(): TemplateResult | string {
    if (!this.panelDetailsProps.sourceCompany || !this.panelDetailsProps.destinationCompany) {
      return html``;
    }
    return html`
      <gz-labeled-content label="${translateFn('Source company')}">
        <gz-text class="labeled-value" fontSize="12">${this.panelDetailsProps.sourceCompany}</gz-text>
      </gz-labeled-content>
      <gz-labeled-content label="${translateFn('Destination company')}">
        <gz-text class="labeled-value" fontSize="12">${this.panelDetailsProps.destinationCompany}</gz-text>
      </gz-labeled-content>
    `;
  }

  private renderEndedOn(): TemplateResult | string {
    return !isEmpty(this.panelDetailsProps.endedOn)
      ? html` <gz-labeled-content label="${translateFn('Ended on')}">
          <gz-text class="labeled-value" fontSize="12">${this.panelDetailsProps.endedOn}</gz-text>
        </gz-labeled-content>`
      : '';
  }

  private renderFileName(): TemplateResult | string {
    return !isEmpty(this.panelDetailsProps.quarantineFileName)
      ? html` <gz-labeled-content label="${translateFn('File name')}">
          <gz-text class="labeled-value" fontSize="12">${this.panelDetailsProps.quarantineFileName}</gz-text>
        </gz-labeled-content>`
      : '';
  }

  private renderPaths(): TemplateResult | string {
    return this.panelDetailsProps.paths && !isEmpty(this.panelDetailsProps.paths)
      ? html` <gz-labeled-content label="${translateFn('Paths')}">
          <div class="d-flex flex-column labeled-value">
            ${this.panelDetailsProps.paths.map(path => this.renderTextWithTooltip(path))}
          </div>
        </gz-labeled-content>`
      : '';
  }

  private renderCommandLines(): TemplateResult | string {
    return this.panelDetailsProps.commandLines && !isEmpty(this.panelDetailsProps.commandLines)
      ? html` <gz-labeled-content label="${translateFn('Command lines')}">
          <div class="d-flex flex-column labeled-value">
            ${this.panelDetailsProps.commandLines.map(commandLine => this.renderTextWithTooltip(commandLine))}
          </div>
        </gz-labeled-content>`
      : '';
  }

  private renderAwsSubscription(): TemplateResult | string {
    if (isEmpty(this.panelDetailsProps.awsSubscriptionName)) {
      return '';
    } else {
      return html` <gz-labeled-content label="${translateFn('AWS integration')}">
        <gz-tooltip tooltipText="${this.panelDetailsProps.awsSubscriptionName}" isEllipsis>
          <gz-text class="labeled-value" fontSize="12">${this.panelDetailsProps.awsSubscriptionName}</gz-text>
        </gz-tooltip>
      </gz-labeled-content>`;
    }
  }

  private renderScheduled(): TemplateResult | string {
    return !isEmpty(this.panelDetailsProps.scheduled)
      ? html` <gz-labeled-content label="${this.panelDetailsProps.scheduledLabel}">
          <gz-text class="labeled-value" fontSize="12">${this.panelDetailsProps.scheduled}</gz-text></gz-labeled-content
        >`
      : '';
  }

  private renderContainerNames(): TemplateResult | string {
    return this.panelDetailsProps.containerNames !== ''
      ? html` <gz-labeled-content label="${translateFn('Container names')}">
          <gz-tooltip tooltipText="${this.panelDetailsProps.containerNames}" isEllipsis
            ><gz-text class="labeled-value" fontSize="12">${this.panelDetailsProps.containerNames}</gz-text></gz-tooltip
          >
        </gz-labeled-content>`
      : '';
  }

  private renderSuspendDuration(): TemplateResult | string {
    if (!this.panelDetailsProps.suspendDuration) {
      return html``;
    }

    const days = Math.floor(this.panelDetailsProps.suspendDuration / (24 * 60));
    const hours = Math.floor((this.panelDetailsProps.suspendDuration % (24 * 60)) / 60);
    const minutes = this.panelDetailsProps.suspendDuration % 60;

    let result = '';
    result += days > 0 ? (days === 1 ? `${days} ${translateFn('day')} ` : `${days} ${translateFn('days')} `) : '';
    result += hours > 0 ? (hours === 1 ? `${hours} ${translateFn('hour')} ` : `${hours} ${translateFn('hours')} `) : '';
    result += minutes > 0 ? (minutes === 1 ? `${minutes} ${translateFn('minute')} ` : `${minutes} ${translateFn('minutes')} `) : '';

    return html`
      <gz-labeled-content label="${translateFn('Suspension period set to')}">
        <gz-text class="labeled-value" fontSize="12">${result}</gz-text>
      </gz-labeled-content>`;
  }

  private renderTextWithTooltip(text: string): TemplateResult | string {
    return html`
      <gz-tooltip tooltipText="${text}" isEllipsis>
        <gz-text class="labeled-value d-inline" fontSize="12">${text}</gz-text>
      </gz-tooltip>
    `;
  }

  private renderButtonsSection(): TemplateResult {
    return html`
      <div class="details-panel-actions">
        <gz-button
          id="close"
          buttonheight="medium"
          buttonfontsize="medium"
          type="full"
          color="primary"
          class="mr-2"
          @click="${this.closePanel}"
          >${this.panelConfigProps.closeButtonLabel}</gz-button
        >
      </div>
    `;
  }

  private closePanel() {
    this.dispatchEvent(
      new CustomEvent(SubTasksEvents.CLOSE_SUBTASK_DETAILS_PANEL, {
        bubbles: true,
        composed: true,
      })
    );
  }
}
